import React, { useState, useEffect } from "react";
import { getData, serverURL, postData } from "../../../Services/Fetchnodeservices";

// ===================== THEME =====================
const getT = (isDark) => ({
  bg: isDark ? "#0c1018" : "#f0f4ff",
  surface: isDark ? "#141824" : "#ffffff",
  surfaceAlt: isDark ? "#1c2133" : "#f4f7ff",
  border: isDark ? "rgba(255,255,255,0.07)" : "#e2e8f5",
  text: isDark ? "#f1f5f9" : "#0f172a",
  textMuted: isDark ? "#64748b" : "#94a3b8",
  textSub: isDark ? "#94a3b8" : "#64748b",
  shadow: isDark ? "0 4px 28px rgba(0,0,0,0.45)" : "0 4px 28px rgba(99,102,241,0.10)",
  shadowSm: isDark ? "0 2px 10px rgba(0,0,0,0.35)" : "0 2px 10px rgba(99,102,241,0.07)",
  accent: "#6c63ff",
  success: "#22c55e",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#3b82f6",
});

// ===================== ICONS =====================
const SearchIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);

const EditIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const PlusIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const CloseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const DocumentIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const RupeeIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 3h12M6 8h12M6 13l4-4M18 13l-4-4M6 18h12" />
  </svg>
);

// ===================== COMPONENTS =====================
const StatusBadge = ({ status, isDark }) => {
  const t = getT(isDark);
  const isActive = status === "1";
  const config = isActive 
    ? { bg: "rgba(34,197,94,0.15)", color: t.success, label: "Active" }
    : { bg: "rgba(239,68,68,0.15)", color: t.danger, label: "Inactive" };
  return (
    <span style={{ padding: "4px 10px", borderRadius: "20px", fontSize: "11px", fontWeight: 600, background: config.bg, color: config.color }}>
      {config.label}
    </span>
  );
};

const Toast = ({ message, type, onClose, isDark }) => {
  const t = getT(isDark);
  return (
    <div style={{ position: "fixed", bottom: "24px", right: "24px", zIndex: 1000, background: t.surface, border: `1px solid ${type === "error" ? t.danger : t.success}`, borderRadius: "12px", padding: "12px 20px", display: "flex", alignItems: "center", gap: "12px", boxShadow: t.shadow }}>
      <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: type === "error" ? t.danger : t.success }} />
      <span style={{ fontSize: "13px", color: type === "error" ? t.danger : t.success }}>{message}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted }}><CloseIcon /></button>
    </div>
  );
};

// ===================== MAIN COMPONENT - MOU_CHARGES =====================
const MOUCharges = ({ isDark = true }) => {
  const t = getT(isDark);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingCharge, setEditingCharge] = useState(null);
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Data state
  const [chargesList, setChargesList] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    agreement_id: "",
    commission_percentage: "20",
    commission_resale_percentage: "10",
    minimum_order: "200",
    platform_charge: "9",
    handling_charge_percentage: "2",
    gst_percentage: "5",
    order_receiving_charges: "1",
    subscription_fee: "299",
    subscription_type: "monthly",
    schedule_mode: "auto",
    status: "1"
  });

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    fetchAllCharges();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isMobile = windowWidth <= 768;

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  // Fetch all charges
  const fetchAllCharges = async () => {
    setLoading(true);
    try {
      const result = await getData("moucharges/fetch_all_charges");
      if (result.status) {
        setChargesList(result.data);
      } else {
        showToast(result.message || "Failed to fetch charges", "error");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      showToast("Network error while fetching charges", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (charge = null) => {
    if (charge) {
      setEditingCharge(charge);
      setFormData({
        id: charge.id,
        agreement_id: charge.agreement_id || "",
        commission_percentage: charge.commission_percentage || "20",
        commission_resale_percentage: charge.commission_resale_percentage || "10",
        minimum_order: charge.minimum_order || "200",
        platform_charge: charge.platform_charge || "9",
        handling_charge_percentage: charge.handling_charge_percentage || "2",
        gst_percentage: charge.gst_percentage || "5",
        order_receiving_charges: charge.order_receiving_charges || "1",
        subscription_fee: charge.subscription_fee || "299",
        subscription_type: charge.subscription_type || "monthly",
        schedule_mode: charge.schedule_mode || "auto",
        status: charge.status || "1"
      });
    } else {
      setEditingCharge(null);
      setFormData({
        agreement_id: "",
        commission_percentage: "20",
        commission_resale_percentage: "10",
        minimum_order: "200",
        platform_charge: "9",
        handling_charge_percentage: "2",
        gst_percentage: "5",
        order_receiving_charges: "1",
        subscription_fee: "299",
        subscription_type: "monthly",
        schedule_mode: "auto",
        status: "1"
      });
    }
    setShowModal(true);
  };

  // Add or Update charge
  const handleSaveCharge = async () => {
    if (!formData.agreement_id) {
      showToast("Agreement ID is required", "error");
      return;
    }

    setSubmitting(true);
    
    try {
      let result;
      if (editingCharge) {
        const formDataObj = {
          id: editingCharge.id,
          agreement_id: formData.agreement_id,
          commission_percentage: formData.commission_percentage,
          commission_resale_percentage: formData.commission_resale_percentage,
          minimum_order: formData.minimum_order,
          platform_charge: formData.platform_charge,
          handling_charge_percentage: formData.handling_charge_percentage,
          gst_percentage: formData.gst_percentage,
          order_receiving_charges: formData.order_receiving_charges,
          subscription_fee: formData.subscription_fee,
          subscription_type: formData.subscription_type,
          schedule_mode: formData.schedule_mode,
          status: formData.status
        };
        result = await postData("moucharges/update_charge", formDataObj);
        if (result.status) {
          showToast("Charges updated successfully");
          fetchAllCharges();
          setShowModal(false);
        } else {
          showToast(result.message || "Update failed", "error");
        }
      } else {
        const formDataObj = {
          agreement_id: formData.agreement_id,
          commission_percentage: formData.commission_percentage,
          commission_resale_percentage: formData.commission_resale_percentage,
          minimum_order: formData.minimum_order,
          platform_charge: formData.platform_charge,
          handling_charge_percentage: formData.handling_charge_percentage,
          gst_percentage: formData.gst_percentage,
          order_receiving_charges: formData.order_receiving_charges,
          subscription_fee: formData.subscription_fee,
          subscription_type: formData.subscription_type,
          schedule_mode: formData.schedule_mode,
          status: formData.status
        };
        result = await postData("moucharges/add_charge", formDataObj);
        if (result.status) {
          showToast("Charges added successfully");
          fetchAllCharges();
          setShowModal(false);
        } else {
          showToast(result.message || "Add failed", "error");
        }
      }
    } catch (error) {
      console.error("Save error:", error);
      showToast("Network error while saving", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // Update status
  const handleToggleStatus = async (charge) => {
    const newStatus = charge.status === "1" ? "0" : "1";
    
    setSubmitting(true);
    try {
      const result = await postData("moucharges/update_status", { 
        id: charge.id, 
        status: newStatus 
      });
      if (result.status) {
        showToast(`Charges ${newStatus === "1" ? "activated" : "deactivated"} successfully`);
        fetchAllCharges();
      } else {
        showToast(result.message || "Status update failed", "error");
      }
    } catch (error) {
      console.error("Status update error:", error);
      showToast("Network error while updating status", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteCharge = async (id) => {
    if (window.confirm("Are you sure you want to delete this charge configuration?")) {
      try {
        const result = await postData("moucharges/delete_charge", { id });
        if (result.status) {
          showToast("Charge configuration deleted successfully");
          fetchAllCharges();
        } else {
          showToast(result.message || "Delete failed", "error");
        }
      } catch (error) {
        console.error("Delete error:", error);
        showToast("Network error while deleting", "error");
      }
    }
  };

  const filteredCharges = chargesList.filter(charge => 
    charge.agreement_id?.toLowerCase().includes(search.toLowerCase()) ||
    charge.commission_percentage?.toString().includes(search)
  );

  const styles = {
    container: {
      minHeight: "100vh",
      background: t.bg,
      fontFamily: "'Inter', 'DM Sans', sans-serif",
      padding: isMobile ? "16px" : "24px",
    },
    contentWrapper: {
      maxWidth: "1400px",
      margin: "0 auto",
    },
    header: {
      marginBottom: "28px",
    },
    title: {
      fontSize: isMobile ? "24px" : "32px",
      fontWeight: 700,
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      margin: 0,
    },
    subtitle: {
      fontSize: "14px",
      color: t.textMuted,
      marginTop: "8px",
    },
    card: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "24px",
      overflow: "hidden",
      boxShadow: t.shadow,
    },
    cardHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "20px 24px",
      borderBottom: `1px solid ${t.border}`,
      background: t.surfaceAlt,
      flexWrap: "wrap",
      gap: "12px",
    },
    cardTitle: {
      fontSize: "18px",
      fontWeight: 700,
      color: t.text,
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    searchWrapper: {
      position: "relative",
      width: isMobile ? "100%" : "280px",
    },
    searchInput: {
      width: "100%",
      padding: "10px 12px 10px 36px",
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "13px",
      color: t.text,
      outline: "none",
    },
    addButton: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      border: "none",
      borderRadius: "12px",
      color: "#fff",
      fontSize: "13px",
      fontWeight: 600,
      cursor: "pointer",
    },
    headerActions: {
      display: "flex",
      gap: "20px",
      alignItems: "center",
      flexWrap: "wrap",
    },
    documentGrid: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fill, minmax(380px, 1fr))",
      gap: "24px",
      padding: "24px",
    },
    documentCard: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "20px",
      overflow: "hidden",
      transition: "all 0.3s ease",
      cursor: "pointer",
      ":hover": {
        transform: "translateY(-4px)",
        boxShadow: t.shadow,
      },
    },
    documentHeader: {
      background: `linear-gradient(135deg, ${t.accent}, ${t.accent}dd)`,
      padding: "16px 20px",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    documentHeaderTitle: {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      color: "#fff",
    },
    documentHeaderText: {
      fontSize: "16px",
      fontWeight: 600,
      color: "#fff",
    },
    documentBody: {
      padding: "20px",
    },
    documentSection: {
      marginBottom: "20px",
    },
    documentSectionTitle: {
      fontSize: "14px",
      fontWeight: 700,
      color: t.accent,
      marginBottom: "12px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      borderBottom: `2px solid ${t.accent}20`,
      paddingBottom: "8px",
    },
    chargesGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "12px",
    },
    chargeItem: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
      borderBottom: `1px solid ${t.border}`,
    },
    chargeLabel: {
      fontSize: "12px",
      fontWeight: 500,
      color: t.textMuted,
    },
    chargeValue: {
      fontSize: "14px",
      fontWeight: 600,
      color: t.text,
    },
    scheduleMode: {
      display: "flex",
      gap: "12px",
      marginTop: "12px",
    },
    modeBadge: (isActive) => ({
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "11px",
      fontWeight: 500,
      background: isActive ? t.success : t.surfaceAlt,
      color: isActive ? "#fff" : t.textMuted,
      border: `1px solid ${isActive ? t.success : t.border}`,
    }),
    documentFooter: {
      padding: "16px 20px",
      borderTop: `1px solid ${t.border}`,
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      background: t.surfaceAlt,
    },
    actionIcon: {
      padding: "6px 12px",
      borderRadius: "8px",
      border: "none",
      fontSize: "11px",
      fontWeight: 500,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "4px",
    },
    loadingText: {
      textAlign: "center",
      padding: "60px",
      color: t.textMuted,
      fontSize: "14px",
    },
    noData: {
      textAlign: "center",
      padding: "60px",
      color: t.textMuted,
    },
    modalOverlay: {
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1000,
      background: "rgba(0,0,0,0.7)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px",
    },
    modal: {
      background: t.surface,
      border: `1px solid ${t.border}`,
      borderRadius: "28px",
      width: isMobile ? "100%" : "700px",
      maxHeight: "85vh",
      display: "flex",
      flexDirection: "column",
      boxShadow: t.shadow,
    },
    modalHeader: {
      padding: "20px 24px",
      borderBottom: `1px solid ${t.border}`,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      flexShrink: 0,
      background: t.surface,
      borderRadius: "28px 28px 0 0",
    },
    modalTitle: {
      fontSize: "20px",
      fontWeight: 700,
      color: t.text,
    },
    modalBody: {
      padding: "24px",
      overflowY: "auto",
      flex: 1,
    },
    modalFooter: {
      padding: "16px 24px",
      borderTop: `1px solid ${t.border}`,
      display: "flex",
      gap: "12px",
      justifyContent: "flex-end",
      flexShrink: 0,
      background: t.surface,
      borderRadius: "0 0 28px 28px",
    },
    formGroup: {
      marginBottom: "20px",
    },
    label: {
      display: "block",
      fontSize: "12px",
      fontWeight: 600,
      color: t.textMuted,
      marginBottom: "6px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },
    input: {
      width: "100%",
      padding: "12px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: t.text,
      outline: "none",
      fontFamily: "inherit",
    },
    select: {
      width: "100%",
      padding: "12px 14px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      fontSize: "14px",
      color: t.text,
      outline: "none",
      cursor: "pointer",
    },
    cancelBtn: {
      padding: "10px 24px",
      background: t.surfaceAlt,
      border: `1px solid ${t.border}`,
      borderRadius: "12px",
      color: t.text,
      cursor: "pointer",
      fontSize: "13px",
      fontWeight: 500,
    },
    saveBtn: {
      padding: "10px 24px",
      background: "linear-gradient(135deg, #6c63ff, #a855f7)",
      border: "none",
      borderRadius: "12px",
      color: "#fff",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "13px",
    },
    grid2: {
      display: "grid",
      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
      gap: "16px",
    },
    sectionCard: {
      background: t.surfaceAlt,
      borderRadius: "16px",
      padding: "20px",
      marginBottom: "20px",
    },
    sectionTitle: {
      fontSize: "14px",
      fontWeight: 600,
      color: t.accent,
      marginBottom: "16px",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    infoBox: {
      background: t.accent + "10",
      borderRadius: "12px",
      padding: "16px",
      marginTop: "20px",
      border: `1px solid ${t.accent}20`,
    },
    infoText: {
      fontSize: "13px",
      color: t.textSub,
      lineHeight: "1.6",
    },
    scheduleToggle: {
      display: "flex",
      gap: "12px",
      marginTop: "8px",
    },
    scheduleOption: (isActive) => ({
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      background: isActive ? t.accent : "transparent",
      border: `1px solid ${isActive ? t.accent : t.border}`,
      borderRadius: "12px",
      fontSize: "13px",
      fontWeight: 500,
      color: isActive ? "#fff" : t.text,
      cursor: "pointer",
    }),
  };

  // Render document card
  const renderDocumentCard = (charge, index) => (
    <div key={charge.id} style={styles.documentCard}>
      <div style={styles.documentHeader}>
        <div style={styles.documentHeaderTitle}>
          <DocumentIcon />
          <span style={styles.documentHeaderText}>MOU Agreement #{charge.agreement_id}</span>
        </div>
        <StatusBadge status={charge.status} isDark={isDark} />
      </div>
      
      <div style={styles.documentBody}>
        {/* Charges & Commission Section */}
        <div style={styles.documentSection}>
          <div style={styles.documentSectionTitle}>
            💰 Your Charges & Commission
          </div>
          <div style={styles.chargesGrid}>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Commission</span>
              <span style={styles.chargeValue}>{charge.commission_percentage}%</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Commission (Resale)</span>
              <span style={styles.chargeValue}>{charge.commission_resale_percentage}%</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Minimum Order</span>
              <span style={styles.chargeValue}>₹{charge.minimum_order}</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Platform Charge</span>
              <span style={styles.chargeValue}>₹{charge.platform_charge}</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Handling Charge</span>
              <span style={styles.chargeValue}>{charge.handling_charge_percentage}%</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>GST</span>
              <span style={styles.chargeValue}>{charge.gst_percentage}%</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Order Receiving Charges</span>
              <span style={styles.chargeValue}>₹{charge.order_receiving_charges}</span>
            </div>
            <div style={styles.chargeItem}>
              <span style={styles.chargeLabel}>Subscription Fee</span>
              <span style={styles.chargeValue}>
                {charge.subscription_type === "monthly" ? "Monthly" : 
                 charge.subscription_type === "quarterly" ? "Quarterly" : "Yearly"} - ₹{charge.subscription_fee}
              </span>
            </div>
          </div>
        </div>

        {/* Operating Schedule Section */}
        <div style={styles.documentSection}>
          <div style={styles.documentSectionTitle}>
            <ClockIcon /> Operating Schedule
          </div>
          <div style={styles.scheduleMode}>
            <span style={styles.modeBadge(charge.schedule_mode === "manual")}>
              Manual
            </span>
            <span style={styles.modeBadge(charge.schedule_mode === "auto")}>
              Auto
            </span>
          </div>
          <div style={{ ...styles.infoBox, marginTop: "12px" }}>
            <div style={styles.infoText}>
              <strong>📌 Note:</strong> Your schedule has been approved by admin and is now locked. Contact support to make changes.
            </div>
          </div>
        </div>
      </div>

      <div style={styles.documentFooter}>
        <button 
          style={{ ...styles.actionIcon, background: t.info, color: "#fff" }}
          onClick={() => handleOpenModal(charge)}
        >
          <EditIcon /> Edit
        </button>
        <button 
          style={{ ...styles.actionIcon, background: charge.status === "1" ? t.warning : t.success, color: "#fff" }}
          onClick={() => handleToggleStatus(charge)}
          disabled={submitting}
        >
          {charge.status === "1" ? "Deactivate" : "Activate"}
        </button>
        <button 
          style={{ ...styles.actionIcon, background: t.danger, color: "#fff" }}
          onClick={() => handleDeleteCharge(charge.id)}
        >
          <DeleteIcon /> Delete
        </button>
      </div>
    </div>
  );

  return (
    <div style={styles.container}>
      <div style={styles.contentWrapper}>
        <div style={styles.header}>
          <h1 style={styles.title}>📋 MOU Agreement Charges & Commission</h1>
          <p style={styles.subtitle}>Configure charges, commission rates, and operating schedules for restaurant partners</p>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHeader}>
            <h3 style={styles.cardTitle}>
              <DocumentIcon /> MOU Agreements Documents
            </h3>
            <div style={styles.headerActions}>
              <div style={styles.searchWrapper}>
                <span style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)" }}><SearchIcon /></span>
                <input 
                  style={styles.searchInput} 
                  placeholder="Search by Agreement ID..." 
                  value={search} 
                  onChange={(e) => setSearch(e.target.value)} 
                />
              </div>
              <button style={styles.addButton} onClick={() => handleOpenModal()}>
                <PlusIcon /> Add Agreement
              </button>
            </div>
          </div>

          {loading ? (
            <div style={styles.loadingText}>Loading agreements...</div>
          ) : filteredCharges.length === 0 ? (
            <div style={styles.noData}>No agreements found. Click "Add Agreement" to create one.</div>
          ) : (
            <div style={styles.documentGrid}>
              {filteredCharges.map((charge, index) => renderDocumentCard(charge, index))}
            </div>
          )}

          <div style={styles.rowCount}>
            Showing {filteredCharges.length} of {chargesList.length} agreements
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>{editingCharge ? "Edit MOU Agreement" : "Create MOU Agreement"}</div>
              <button onClick={() => setShowModal(false)} style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, padding: "8px", borderRadius: "8px" }}>
                <CloseIcon />
              </button>
            </div>
            <div style={styles.modalBody}>
              {/* Agreement ID */}
              <div style={styles.formGroup}>
                <label style={styles.label}>Agreement ID *</label>
                <input 
                  style={styles.input} 
                  value={formData.agreement_id} 
                  onChange={(e) => setFormData({ ...formData, agreement_id: e.target.value })}
                  placeholder="e.g., MOU_001"
                />
              </div>

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Commission (%)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.commission_percentage} 
                    onChange={(e) => setFormData({ ...formData, commission_percentage: e.target.value })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Commission (Resale) (%)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.commission_resale_percentage} 
                    onChange={(e) => setFormData({ ...formData, commission_resale_percentage: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Minimum Order (₹)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.minimum_order} 
                    onChange={(e) => setFormData({ ...formData, minimum_order: e.target.value })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Platform Charge (₹)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.platform_charge} 
                    onChange={(e) => setFormData({ ...formData, platform_charge: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Handling Charge (%)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.handling_charge_percentage} 
                    onChange={(e) => setFormData({ ...formData, handling_charge_percentage: e.target.value })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>GST (%)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.gst_percentage} 
                    onChange={(e) => setFormData({ ...formData, gst_percentage: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Order Receiving Charges (₹)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.order_receiving_charges} 
                    onChange={(e) => setFormData({ ...formData, order_receiving_charges: e.target.value })}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Subscription Fee (₹)</label>
                  <input 
                    type="number" 
                    style={styles.input} 
                    value={formData.subscription_fee} 
                    onChange={(e) => setFormData({ ...formData, subscription_fee: e.target.value })}
                  />
                </div>
              </div>

              <div style={styles.grid2}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Subscription Type</label>
                  <select style={styles.select} value={formData.subscription_type} onChange={(e) => setFormData({ ...formData, subscription_type: e.target.value })}>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Status</label>
                  <select style={styles.select} value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })}>
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Schedule Mode Section */}
              <div style={styles.sectionCard}>
                <div style={styles.sectionTitle}>
                  <ClockIcon /> Operating Schedule
                </div>
                <div style={styles.scheduleToggle}>
                  <div 
                    style={styles.scheduleOption(formData.schedule_mode === "manual")}
                    onClick={() => setFormData({ ...formData, schedule_mode: "manual" })}
                  >
                    Manual
                  </div>
                  <div 
                    style={styles.scheduleOption(formData.schedule_mode === "auto")}
                    onClick={() => setFormData({ ...formData, schedule_mode: "auto" })}
                  >
                    Auto
                  </div>
                </div>
                <div style={styles.infoBox}>
                  <div style={styles.infoText}>
                    <strong>📌 Note:</strong> Your schedule has been approved by admin and is now locked. Contact support to make changes.
                  </div>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancel</button>
              <button style={styles.saveBtn} onClick={handleSaveCharge} disabled={submitting}>
                {submitting ? "Saving..." : (editingCharge ? "Update Agreement" : "Create Agreement")}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} isDark={isDark} />}
    </div>
  );
};

export default MOUCharges;